import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Quiz State Hook
export const useQuizState = () => {
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [answers, setAnswers] = useState({
    // Page 1 - Présentation
    nom: '',
    anciennete: 3,
    equipe: 5,
    experience: 8,
    
    // Page 2 - Concept
    type_cuisine: null,
    ticket_moyen: 18,
    couverts: 60,
    concurrence: 60,
    
    // Page 3 - Marque
    concept_clair: 50,
    marque_reconnaissable: 40,
    plats_signature: 50,
    proposition_valeur: 50,
    histoire_marque: 30,
    coherence_marque: 50,
    
    // Page 4 - Finances
    ca_annuel: 300,
    objectif_ca_6mois: 20,
    coef: 3,
    food_cost: 30,
    marge_satisfaction: 50,
    tresorerie: 50,
    
    // Page 5 - Temps
    heures: 55,
    repos: 1,
    temps: { terrain: 70, admin: 15, dev: 10, perso: 5 },
    
    // Page 6 - Canaux de vente
    ca_surplace: 60,
    ca_emporter: 15,
    ca_clickcollect: 5,
    ca_livraison: 15,
    ca_livraison_propre: 0,
    ca_traiteur: 5,
    dependance_plateformes: 40,
    
    // Page 7 - Opérations
    process: 30,
    stocks: 50,
    qualite: 70,
    pertes: 50,
    optimisation_carte: 50,
    
    // Page 8 - Équipe
    autonomie: 40,
    recrutement: 70,
    turnover: 50,
    formation: 40,
    ambiance: 7,
    primes: 30,
    
    // Page 9 - Digital
    reseaux: 50,
    note_google: 4.2,
    nb_avis: 100,
    ia: 20,
    site_web: 40,
    budget_marketing: 300,
    
    // Page 10 - Clients
    satisfaction: 7,
    retour_clients: 40,
    gestion_avis: 50,
    fidelite: 20,
    crm: 20,
    
    // Page 11 - Profil
    profil_terrain: 70,
    obj_ca: 70,
    obj_marge: 80,
    obj_liberer: 60,
    obj_scale: 30,
    obj_marque: 50,
    stress: 60,
    
    // Page 12 - Contact
    prenom: '',
    telephone: ''
  });
  
  const updateAnswer = useCallback((key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  }, []);
  
  const updateTemps = useCallback((key, value) => {
    setAnswers(prev => ({
      ...prev,
      temps: { ...prev.temps, [key]: value }
    }));
  }, []);
  
  const toggleMultipleChoice = useCallback((key, value) => {
    setAnswers(prev => {
      const current = prev[key] || [];
      if (current.includes(value)) {
        return { ...prev, [key]: current.filter(v => v !== value) };
      }
      return { ...prev, [key]: [...current, value] };
    });
  }, []);
  
  const nextQuestion = useCallback(() => {
    setCurrentQuestion(prev => Math.min(prev + 1, 12));
  }, []);
  
  const prevQuestion = useCallback(() => {
    setCurrentQuestion(prev => Math.max(prev - 1, 1));
  }, []);
  
  return {
    currentQuestion,
    setCurrentQuestion,
    answers,
    setAnswers,
    updateAnswer,
    updateTemps,
    toggleMultipleChoice,
    nextQuestion,
    prevQuestion
  };
};

// API Hooks
export const useAuditAPI = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const createAudit = async (answers) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/audits`, { answers });
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const getAudit = async (auditId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/audits/${auditId}`);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Audit non trouvé');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const sendEmail = async (auditId, email) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/audits/${auditId}/send-email`, {
        audit_id: auditId,
        recipient_email: email
      });
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'envoi');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  return { createAudit, getAudit, sendEmail, loading, error };
};

export const usePaymentAPI = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const createCheckoutSession = async (auditId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/checkout/session`, {
        audit_id: auditId,
        origin_url: window.location.origin
      });
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de paiement');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const checkPaymentStatus = async (sessionId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de vérification');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  return { createCheckoutSession, checkPaymentStatus, loading, error };
};

export const useEbookAPI = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const verifyAccessCode = async (code) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/ebook/verify-code`, {
        access_code: code
      });
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Code invalide');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const generateEbook = async (auditId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/ebook/generate`, {
        audit_id: auditId
      });
      return response.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de génération');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  return { verifyAccessCode, generateEbook, loading, error };
};

// Local storage hook for persisting quiz state
export const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      return initialValue;
    }
  });
  
  const setValue = (value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(error);
    }
  };
  
  return [storedValue, setValue];
};
